/*
 * AT.drawer — shared drawer/modal helper for the PDP (size, colour, details).
 *
 * Namespaced on window.AT.drawer. No build step; plain IIFE.
 * Handles: open/close, dialog aria, focus trap, ESC, backdrop + [data-drawer-close]
 * dismissal, body scroll-lock, and focus restore.
 *
 *   var d = AT.drawer.create(drawerEl, { openers: [btn], backdrop: el, label: 'Colour' });
 *   d.open(); d.close();
 *
 * The drawer + backdrop are moved to <body> on create (opt out with portal: false),
 * so style them with top-level selectors, not descendants of their authored parent.
 */
(function initAtDrawer() {
  window.AT = window.AT || {};
  if (window.AT.drawer) return;

  var focusableIn = function focusableIn(el) {
    return el.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
  };

  var create = function createDrawer(drawer, opts) {
    opts = opts || {};
    var backdrop = opts.backdrop || null;
    var openers = opts.openers || [];
    var label = opts.label || '';
    var lastFocused = null;
    var open = false;

    // Portal to <body>: drawers authored inside a sticky/transformed ancestor would
    // be trapped in its stacking context, beneath the fixed header.
    if (opts.portal !== false) {
      if (backdrop) document.body.appendChild(backdrop);
      document.body.appendChild(drawer);
    }

    var onKeydown = function onDrawerKeydown(ev) {
      if (!open) return;
      if (ev.key === 'Escape') {
        ev.preventDefault();
        close();
        return;
      }
      if (ev.key !== 'Tab') return;
      var els = focusableIn(drawer);
      if (!els.length) return;
      var first = els[0];
      var last = els[els.length - 1];
      if (ev.shiftKey && document.activeElement === first) {
        ev.preventDefault();
        last.focus();
      } else if (!ev.shiftKey && document.activeElement === last) {
        ev.preventDefault();
        first.focus();
      }
    };

    function doOpen() {
      if (open) return;
      open = true;
      lastFocused = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      if (label) drawer.setAttribute('aria-label', label);
      openers.forEach(function (o) {
        o.setAttribute('aria-expanded', 'true');
      });
      if (backdrop) {
        backdrop.hidden = false;
        requestAnimationFrame(function () {
          backdrop.classList.add('is-open');
        });
      }
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', onKeydown);
      var focusTarget = drawer.querySelector('[data-drawer-close]') || focusableIn(drawer)[0];
      if (focusTarget) focusTarget.focus({ preventScroll: true });
      if (opts.onOpen) opts.onOpen();
    }

    function close() {
      if (!open) return;
      open = false;
      drawer.classList.remove('is-open');
      drawer.removeAttribute('role');
      drawer.removeAttribute('aria-modal');
      openers.forEach(function (o) {
        o.setAttribute('aria-expanded', 'false');
      });
      if (backdrop) {
        backdrop.classList.remove('is-open');
        setTimeout(function () {
          if (!open) backdrop.hidden = true;
        }, 300);
      }
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', onKeydown);
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus({ preventScroll: true });
      }
      if (opts.onClose) opts.onClose();
    }

    openers.forEach(function (o) {
      o.addEventListener('click', doOpen);
    });
    drawer.querySelectorAll('[data-drawer-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });
    if (backdrop) backdrop.addEventListener('click', close);

    return {
      open: doOpen,
      close: close,
      isOpen: function () {
        return open;
      },
      el: drawer,
    };
  };

  window.AT.drawer = { create: create };
})();
